/**
 * 生成ステート周りの型定義。
 *
 * useGenerationState.tsx から切り出したもの (定義内容は変更なし)。
 */
import type { BannerLayout } from '../../../features/bannerLayout/types';
import type { MotionCandidate, MotionStop } from '../../../features/motion/jobs';
import type { LayerStack } from '../../../features/motion/layerStack';
import type { ImageOutputFormat, ImageBackgroundMode } from './imageFormat';
import { DEFAULT_VIDEO_MODEL, type VideoModelType } from './modelCatalog';

export type GenerationAssetUsage = 'reference' | 'first_frame' | 'last_frame' | 'reference_video' | 'reference_audio';

/** 動画素材 (usage='reference_video') を参考素材としてどう使うか。
 *  'video' = 動画そのまま (video-to-video) / 'first_frame' / 'last_frame' = 抽出した
 *  静止画フレームを参考画像として送る。未設定は 'video' 扱い。 */
export type VideoReferenceMode = 'video' | 'first_frame' | 'last_frame';

/** 動画素材から抽出済みのフレーム素材 (temporary_materials 行への参照)。 */
export type VideoFrameMaterialRef = { id: string; url: string };

/** 参照音声の用途 (docs/features/reference-audio-roles-plan.md §2.1)。
 *  声/BGM/効果音/サウンドロゴの4値。「未指定」は存在しない (計画書の方針) が、
 *  FE側では推定できるまでの一時的な未選択状態を undefined で表す。 */
export type AudioReferenceUsage = 'voice' | 'bgm' | 'sound_effect' | 'sound_logo';

/** 参照音声の参照元の種類 (計画書 §2.2 `source.kind`)。'material' = org素材
 *  (temporary_materials/materials。従来どおり id = 素材ID)。'audio_catalog' =
 *  運営提供SEカタログ (フェーズ5b。id = audio_catalog_items.id で名前空間が異なる)。
 *  未設定は 'material' 扱い (既存の音声素材チップとの後方互換)。 */
export type AudioSourceKind = 'material' | 'audio_catalog';

/** 声用途の人物バインド対象の種類 (計画書 §2.2/§2.3)。
 *  'scenario_character' はシナリオ経路開放時の予約値のためFEでは未対応。 */
export type AudioVoiceBindingKind = 'actor' | 'seedance_preset';

/** 参照音声の人物バインド (usage='voice' のときのみ意味を持つ。省略=ナレーター声)。 */
export type AudioVoiceBinding = { kind: AudioVoiceBindingKind; id: string };

// ナレ/セリフ/テロップ(フェーズ6)のツリー由来 option。value=option key, mode=metadata.mode。
export type BehaviorToggleOption = { value: string; label: string; mode: string | null };

export type GenerationAsset = {
  id: string;
  thumbnailUrl: string;
  type: 'temporary' | 'cloud';
  /** 素材の表示名 (素材化時に商品画像名から初期コピーされる)。統合タイルのキャプションに焼き込む。 */
  name?: string | null;
  sourceUrl?: string;
  storageBucket?: string | null;
  storagePath?: string | null;
  contentType?: string | null;
  usage?: GenerationAssetUsage;
  asin?: string | string[] | null;
  hasProductLinks?: boolean;
  width?: number | null;
  height?: number | null;
  aspectRatio?: string | null;
  /** 動画素材の尺(秒)。video-to-video の参照動画で出力尺を連動させるのに使う。 */
  durationSeconds?: number | null;
  /** 動画素材に音声トラックがあるか (generation_metadata.has_audio 由来)。
   *  video-to-video で出力の音声有無を参照動画に連動させるのに使う。不明な素材は null。 */
  hasAudio?: boolean | null;
  /** 音声素材の表示テキスト (generation_metadata.narration_text / song_title 由来)。
   *  選択素材チップの波形プレビューにオーバーレイ表示する。 */
  audioText?: string | null;
  /** 動画素材を参考素材としてどう使うか (usage='reference_video' のときのみ意味を持つ)。 */
  videoReferenceMode?: VideoReferenceMode;
  /** 抽出済みフレーム素材のキャッシュ。モード切替の往復で再抽出しないために保持する。
   *  不変条件: videoReferenceMode が 'first_frame'/'last_frame' のとき、対応する
   *  position のエントリは必ず存在する (抽出成功後にのみモードを切り替える)。 */
  videoFrameMaterials?: Partial<Record<'first' | 'last', VideoFrameMaterialRef>>;
  /** 素材の取得元 (source_type)。参照音声の用途の既定値推定に使う
   *  (docs/features/reference-audio-roles-plan.md §2.5。narration→声 / bgm→BGM /
   *  sound_logo→サウンドロゴ)。音声以外の素材では未使用。 */
  sourceType?: string | null;
  /** 素材の既定用途 (materials.default_audio_role 由来。バックエンド未実装の間は常に
   *  undefined で、その場合は sourceType 推定にフォールバックする。§2.5 優先順位2)。 */
  defaultAudioRole?: AudioReferenceUsage | null;
  /** 参照音声としてこの生成でどう使うか (usage='reference_audio' のときのみ意味を持つ)。
   *  添付時にユーザーが明示選択した値、または §2.5 の優先順位で推定した値。未選択
   *  (推定もできない) の間は undefined で、生成前にユーザーへ選択を要求する。 */
  audioUsage?: AudioReferenceUsage;
  /** 声用途の人物バインド (usage='voice' のときのみ意味を持つ)。省略/null=ナレーター声。 */
  audioVoiceBinding?: AudioVoiceBinding | null;
  /** 参照音声の参照元の種類 (usage='reference_audio' のときのみ意味を持つ。§2.2)。
   *  'audio_catalog' の素材は id が audio_catalog_items.id であり、org素材向けの処理
   *  (素材詳細プレビュー・@メンション・覆面化等) に流し込んではならない。未設定は
   *  'material' 扱い (既存の音声素材チップとの後方互換)。 */
  sourceKind?: AudioSourceKind;
  /** レイヤー素材の LayerStack (素材の generation_metadata.layer_stack を parseLayerStack で
   *  検証したもの。形が崩れていれば null)。モーショングラフィックス (プロンプト入力モードの
   *  3 タブ目) の入力判定とレイヤー一覧に使う (docs/features/motion-graphics-mode-plan.md §3.3・§3.5)。 */
  layerStack?: LayerStack | null;
};

/** ACP: テンプレ option ノードに付与されるクリエイターの公開バッジ情報。
 *  バックエンドの attach_creators_to_tree が active クリエイターのみ付与する。 */
export type AcpCreatorBadge = {
  slug: string;
  display_name: string;
  rank_code: string;
  rank_label?: string | null;
  avatar_url?: string | null;
};

export type AttributeTreeNode = {
  id: string;
  key: string;
  label: string;
  type: 'group' | 'option' | 'numeric';
  sort_order?: number;
  metadata?: Record<string, unknown> | null;
  prompt_text?: string | null;
  is_sensitive?: boolean;
  section?: string | null;
  section_order?: number | null;
  is_prompt_prefix?: boolean;
  /** 主表示 or 詳細設定エキスパンド (背景・構図直下のフィールド group のみ解釈)。 */
  placement?: 'primary' | 'advanced';
  /** 組織別の初期選択 option key (override 由来・背景・構図直下の group のみ解釈)。 */
  default_option_key?: string | null;
  /** ACP クリエイター (テンプレ option のみ・active のみ付与)。 */
  creator?: AcpCreatorBadge | null;
  /** フロント内部で付与する "所属セクションキー" (orderedAttributeTree で設定)。 */
  __section?: string | null;
  children?: AttributeTreeNode[];
};

// 人物属性 (gender/ethnicity/age) は attributes.person 配下のみで管理する。
// settings 直下のフラット定義は廃止済み (バックエンドへの重複送信でプロンプト混入
// 事故の一因になったため)。再追加しないこと。
export type GenerationSettings = {
  attributes: Record<string, unknown>;
  background: string;
  hand: 'none' | 'left' | 'right';
  orientation: string;
  effect: string;
  aspect_ratio: string;
  model?: string;
  modelMeta?: Record<string, unknown> | null;
  media_platform_id?: string | null;
  media_menu_id?: string | null;
  media_spec_id?: string | null;
  media_guidelines?: string[];
  media_menu_guidelines?: string[];
  generation_model?: string; // 画像生成モデル (gemini-nano-banana-2.1 | gemini-3-pro-image-preview | gemini-3.1-flash-lite-image | gpt-image-*)
  image_size?: string; // 解像度 (1K | 2K | 4K)。OpenAI モデルでは無視される
  output_format?: ImageOutputFormat; // 出力画像形式。Gemini は png 固定、OpenAI は png/jpeg/webp
  image_background?: ImageBackgroundMode; // 背景指定。'transparent' は GPT Image + PNG/WebP 限定。payload では 'background' にマップ
  target_width?: number; // auto-trim 時の目標幅 (spec 派生または custom 入力)
  target_height?: number; // auto-trim 時の目標高さ
  exclude_text_response?: boolean; // テキストレスポンスを含めないかどうか
  include_logo?: boolean; // ブランドロゴを含めるかどうか (両モード共通・localStorage に保存)
  // プロンプト入力モードの「AIに送る情報」トグル (既定 OFF・localStorage に保存)。属性モードは自動で渡すので使わない
  include_product_info?: boolean; // 商品の登録情報 (参考情報 JSON) を渡すかどうか
  include_brand_info?: boolean; // ブランド情報 (名前 / 説明 / カラー / レギュレーション / ガイドライン画像) を渡すかどうか
  auto_trim_resolution?: boolean; // 規定の解像度に自動リサイズするかどうか（デフォルトtrue。プロパティ名は歴史的経緯で trim のまま）
  // スタジオでルールごとに ON/OFF を上書きしたときの保持先。
  // キー = compliance_rulesets.code、値 = boolean (ユーザーが明示的に切り替えた値)。
  // キーが存在しないルール = 上書きなし → 商品デフォルト / 組織自動判定にフォールバック。
  compliance_overrides?: Record<string, boolean>;
  // バナーレイアウト (docs/features/banner-layout-template-plan.md)。属性モードで適用中のときだけ入る。
  // localStorage には保存しない (個人テンプレートがあるため)。null / undefined = 未設定
  banner_layout?: BannerLayout | null;
  // 適用したレイアウトの名前 (読み込んだプリセット / テンプレートの名前。白紙から作ったら null)。入口の表示用
  banner_layout_name?: string | null;
};

/** 動画生成の共通設定（localStorage 'videoSettings' に永続化）
 *
 * duration / resolution / aspectRatio は generation_models.supported_* で
 * 動的に決まる値域を許容するため string / number に緩和済み。
 * UI 側でモデル切替時に有効値を正規化する。
 */
export interface VideoSettings {
  duration: number;
  resolution: string;
  generateAudio: boolean;
  resizeMode: 'pad' | 'crop';
  aspectRatio: string;
  videoModel: VideoModelType;
  /** 動画生成(reference-to-video)の参照画像にキャラクターシートを含めるか。
   *  AIモデルが人物を弾くケースが多いため Off にできる。既定 true。 */
  includeCastingSheet: boolean;
}

export const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
  duration: 4,
  resolution: '720',
  // 音声生成は常時オン。有効な動画モデル (Seedance / Gemini Omni / MiniMax H3) は
  // いずれも音声有無で料金が変わらないため、2026-09-14 に UI のトグルを撤廃し
  // 常に true を送る (フィールドは料金解決ヘルパーや履歴表示の互換のため残す)。
  generateAudio: true,
  resizeMode: 'crop',
  aspectRatio: '16:9',
  videoModel: DEFAULT_VIDEO_MODEL,
  includeCastingSheet: true,
};

export const STORAGE_KEY_VIDEO_SETTINGS = 'videoSettings';

export function loadVideoSettingsFromStorage(): VideoSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_VIDEO_SETTINGS);
    if (!raw) return DEFAULT_VIDEO_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<VideoSettings>;
    return {
      duration: parsed.duration ?? DEFAULT_VIDEO_SETTINGS.duration,
      resolution: parsed.resolution ?? DEFAULT_VIDEO_SETTINGS.resolution,
      // 音声トグル撤廃前に保存された false を引き継がない (常時オン)。
      generateAudio: DEFAULT_VIDEO_SETTINGS.generateAudio,
      resizeMode: parsed.resizeMode ?? DEFAULT_VIDEO_SETTINGS.resizeMode,
      aspectRatio: parsed.aspectRatio ?? DEFAULT_VIDEO_SETTINGS.aspectRatio,
      videoModel: (parsed.videoModel as VideoModelType) ?? DEFAULT_VIDEO_SETTINGS.videoModel,
      includeCastingSheet: parsed.includeCastingSheet ?? DEFAULT_VIDEO_SETTINGS.includeCastingSheet,
    };
  } catch {
    return DEFAULT_VIDEO_SETTINGS;
  }
}

export type ProductionMode = 'scenario' | 'prompt';
export type VideoGenerationMode = 'frame' | 'reference' | 'storyboard' | 'motion' | null;
export type ScenarioInputTab = 'product' | 'manual';

// 生成キューのジョブ型
export type ActiveJob = {
  jobId: string;
  status: 'polling' | 'completed' | 'failed';
  // polling 中、バックエンドが pending (= 同時実行枠の順番待ち) の間 true。
  // processing (= 実際に生成中) になると false。UI で「順番待ち中…」と「生成中…」を出し分ける。
  // (docs/features/seedance-mini-concurrency-queueing.md §9.6)
  waiting?: boolean;
  startedAt: number;
  type?:
    | 'image'
    | 'video'
    | 'scene-preview'
    | 'concat-video'
    | 'storyboard'
    | 'scenario'
    | 'youtube-upload'
    // 声の台本 (motion-v2) の声の生成 / 1 行の撮り直し (hooks/useVoiceLineHandlers)
    | 'voice'
    // 動画エディタのジョブ (features/videoEditor/hooks/useEditorJobQueue)。AI 編集の提案 / 素材の解析 / MP4 書き出し
    | 'video-edit-propose'
    | 'video-edit-analyze'
    | 'video-edit-render';
  // バックエンドが返す実進捗 (0-100)。指定があれば GenerationQueueWindow は経過時間ベースの疑似進捗の
  // 代わりにこれを使う (動画エディタの解析 / 書き出しなど、進捗を返すジョブ向け)。
  progress?: number;
  // polling 中の処理段階ラベル (例: '動画を生成中' / 'YouTubeにアップロード中')。
  // バックエンドの progress を onProgress で受けて出し分けるジョブで使用。
  // モーショングラフィックスのジョブは backend の stage 通知 (result_data.stage.text) をここに写し、
  // GenerationQueueWindow がラベルの下に出す (2026-09-29)。
  stage?: string;
  // モーショングラフィックスのジョブの途中経過 (docs/features/brand-cut-motion-graphics-plan.md §10.10.1)。
  // 状態 API の result_data.candidates (check が通った版のコンタクトシートと採点) を写す。
  // GenerationQueueWindow が版ごとのサムネイルと「この版にする」「ここまでで決める」を出す。
  candidates?: MotionCandidate[];
  // 利用者の中断 (result_data.motion_stop)。受け付け済みなら段階表示を「版 n を描画します」等に替える。
  motionStop?: MotionStop | null;
  // ストーリーボード生成など、シナリオ単位で進捗を引き継ぐジョブで使用。
  // シナリオ切替で生成コンポーネントが unmount されても、この id を見て
  // 「どのシナリオが生成中か」を復元する。
  scenarioId?: string;
  // 利用者の操作待ちで止まっている (全編モーションの BGM 先行フローで BGM ができて「このBGMでシナリオ生成」を待つ間等)。
  // true の間はステータスバー・生成キュー・タブの生成中表示に数えない (ポーリングは続く)。利用者指示 2026-10-05
  paused?: boolean;
  label?: string;
  // 集約通知バッチ ID。set されているジョブは個別の完了通知を出さず、バッチ全体が
  // 完了した時点で 1 本だけ通知する。用途は 2 系統:
  //   - 絵コンテ自動生成 (storyboardBatches): 全シナリオの全絵コンテ完了で通知
  //   - フレーム一括生成 (frameNotifyBatchIds): 全フレーム完了で通知
  // バッチ状態はいずれもメモリ上のみで永続化しない。リロードで消失した場合は
  // 「バッチ不在」となり、集約点で個別通知へフォールバックする (通知消失を防ぐ)。
  notifyBatchId?: string;
  // 動画ジョブの所要時間予測 (進捗バー) 用。生成に使うモデルと出力尺(秒)。
  // GenerationQueueWindow が ETA テーブルと突き合わせて完了予定を出す。
  videoModel?: string;
  durationSeconds?: number;
  error?: string;
  result?: {
    id: string;
    material_id?: string;
    image_url?: string;
    [key: string]: unknown;
  };
};

// 動画エディタの MP4 書き出し (type: 'video-edit-render') が完了したときに result へ入れる情報。
// useExportJob が complete / patchResult で渡し、GenerationQueueWindow が項目に
// ファイル名・解像度・サイズ・ラウドネス注記を出し、クリックで再ダウンロードする
// (旧「書き出し状況」ポップオーバーの中身をキューの項目へ統合したもの)。
export type VideoEditRenderQueueResult = {
  material_id: string;
  /** ダウンロード時のファイル名 (例: シナリオ名_編集名.mp4)。 */
  filename: string;
  width: number;
  height: number;
  fps: number;
  durationSec: number;
  /** ラウドネス正規化の注記 (掛けようとしたときだけ。無効なプロジェクトでは省略)。 */
  loudnessNote?: string;
  /** 成果物のサイズ (バイト)。完了後の取得が済んでから patchResult で入る。 */
  sizeBytes?: number;
  /** 完了後の成果物取得に失敗したときのメッセージ (項目クリックで再取得できる)。 */
  downloadError?: string;
};

// 絵コンテ自動生成の集約通知バッチ。
// 「ストーリーボード画像を生成する」ON のとき、シナリオ生成完了時点では通知せず、
// 全シナリオの全絵コンテ生成が確定 (成功/失敗問わず) した時点で 1 本だけ通知する。
// 絵コンテジョブは非同期・逐次 (シェルフローは ready ごと) に投入されるため、
// activeJobs だけでは「全部終わったか」を誤判定する。スロット方式で同期的に数える。
export type StoryboardNotifyBatch = {
  id: string;
  // 未確定スロット数。投入予定/実行中の絵コンテジョブ。0 で全確定。
  pending: number;
  // これまでに登録したスロット総数。0 のままなら絵コンテジョブが 1 本も走らなかった
  // (= シナリオ完了通知へフォールバックする) ことを示す。
  total: number;
  // 全スロットの投入が確定したか。true かつ pending===0 で完了通知を出す。
  closed: boolean;
};
// キャスティング（俳優選択）の型
export type SelectedCasting = {
  id: string;
  display_name: string;
  face_image_public_url?: string;
  character_sheet_public_url?: string;
  gender?: string;
  min_age?: number;
  max_age?: number;
  ethnicity?: string;
} | null;

// SelectedCasting の非 null 版 (additionalCastings の要素・フルキャスト配列で使う)。
export type CastingActor = NonNullable<SelectedCasting>;

// キャスト編集状態 (docs/features/character-casting-consistency-plan.md 修正2-a)。
// 'inherited': シナリオの scenarios.casting_model_ids から復元されたまま、ユーザーが
//   まだこの画面でキャスティングを編集していない状態。
// 'explicit': ユーザーがキャスティングを編集した (俳優選択/追加/解除・Seedanceプリセット
//   選択・?model= ディープリンクによる事前選択) 状態。DB からの再取得・再同期で
//   上書きしてはならない。
export type CastingSelectionState = 'inherited' | 'explicit';

// Seedance 専用 BytePlus プリセットデジタルキャラクター選択の型。
// 動画モデルが `seedance-*` のとき AI俳優選択 UI の代わりに表示される。
// SelectedCasting とは排他: 動画モデル切替時に互いに clear される。
export type SelectedSeedancePresetActor = {
  id: string;
  byteplus_asset_id: string;
  display_name: string;
  thumbnail_public_url?: string;
  gender?: string;
  min_age?: number;
  max_age?: number;
  ethnicity?: string;
} | null;

// SelectedSeedancePresetActor の非 null 版 (additionalSeedancePresetActors の要素で使う)。
export type SeedancePresetActorItem = NonNullable<SelectedSeedancePresetActor>;
